import {
  GlobalOutlined,
  MailOutlined,
  PhoneOutlined,
  QuestionCircleOutlined,
  RightOutlined,
  WhatsAppOutlined,
} from '@ant-design/icons';
import React, { useEffect, useState } from 'react';

import AdvancedHeader from '@portal/components/AdvancedHeader/AdvancedHeader';
import AdvancedPanelContentHeader from '@portal/components/AdvancedPanelContentHeader/AdvancedPanelContentHeader';
import EmptyStateSection from '@portal/components/EmptyStateSection/EmptyStateSection';
import ThemedText from '@portal/components/ThemedText/ThemedText';
import { getRouteStackPath } from '@portal/config/routes';
import { useNavigation } from '@portal/hooks/useNavigation';
import { translate } from '@portal/services/i18n';

import { FaqActions } from '@portal/actions';
import useReduxState from '@portal/hooks/useReduxState';
import { useDispatch } from 'react-redux';
import './Faq.less';

const Faq = () => {
  const navigate = useNavigation();
  const dispatch = useDispatch();
  const {
    faq: { faqTopics, contact },
  } = useReduxState();

  const [expandedTopicId, setExpandedTopicId] = useState<string | null>(null);

  useEffect(() => {
    dispatch(FaqActions.getLoggedFaq());
  }, [dispatch]);

  const handleToggleTopic = (topicId: string) => {
    setExpandedTopicId((prev) => (prev === topicId ? null : topicId));
  };

  const sanitizePhone = (value: string) => value.replace(/\D/g, '');

  const renderContact = () => {
    if (!contact) {
      return null;
    }

    const items = [
      contact.phone
        ? {
            key: 'phone',
            icon: <PhoneOutlined />,
            label: translate('PAGES.MY_ACCOUNT.FAQ.CONTACT.PHONE'),
            value: contact.phone,
            href: `tel:${sanitizePhone(contact.phone)}`,
          }
        : null,
      contact.whatsapp
        ? {
            key: 'whatsapp',
            icon: <WhatsAppOutlined />,
            label: translate('PAGES.MY_ACCOUNT.FAQ.CONTACT.WHATSAPP'),
            value: contact.whatsapp,
            href: `https://wa.me/${sanitizePhone(contact.whatsapp)}`,
            external: true,
          }
        : null,
      contact.email
        ? {
            key: 'email',
            icon: <MailOutlined />,
            label: translate('PAGES.MY_ACCOUNT.FAQ.CONTACT.EMAIL'),
            value: contact.email,
            href: `mailto:${contact.email}`,
          }
        : null,
      contact.website
        ? {
            key: 'website',
            icon: <GlobalOutlined />,
            label: translate('PAGES.MY_ACCOUNT.FAQ.CONTACT.WEBSITE'),
            value: contact.website,
            href: /^https?:\/\//i.test(contact.website)
              ? contact.website
              : `https://${contact.website}`,
            external: true,
          }
        : null,
    ].filter((item): item is NonNullable<typeof item> => item !== null);

    if (items.length === 0) {
      return null;
    }

    return (
      <div className="faq-contact">
        <ThemedText variant="h3" className="faq-contact__title">
          {translate('PAGES.MY_ACCOUNT.FAQ.CONTACT.TITLE')}
        </ThemedText>
        <ThemedText variant="body" className="faq-contact__description">
          {translate('PAGES.MY_ACCOUNT.FAQ.CONTACT.DESCRIPTION')}
        </ThemedText>
        <div className="faq-contact__list">
          {items.map((item) => (
            <a
              key={item.key}
              href={item.href}
              className="faq-contact__item"
              target={item.external ? '_blank' : undefined}
              rel={item.external ? 'noopener noreferrer' : undefined}
            >
              <span className="faq-contact__icon">{item.icon}</span>
              <span className="faq-contact__body">
                <ThemedText variant="caption" className="faq-contact__label">
                  {item.label}
                </ThemedText>
                <ThemedText variant="body" className="faq-contact__value">
                  {item.value}
                </ThemedText>
              </span>
              <RightOutlined className="faq-contact__chevron" />
            </a>
          ))}
        </div>
      </div>
    );
  };

  const renderContent = () => {
    if (faqTopics?.length === 0) {
      return (
        <EmptyStateSection
          icon={<QuestionCircleOutlined />}
          title={translate('PAGES.MY_ACCOUNT.FAQ.EMPTY.TITLE')}
          description={translate('PAGES.MY_ACCOUNT.FAQ.EMPTY.DESCRIPTION')}
        />
      );
    }

    return (
      <div className="faq-topics">
        {Array.isArray(faqTopics) &&
          faqTopics.map((topic) => {
            const isOpen = expandedTopicId === topic.id;
            return (
              <div
                key={topic.id}
                className={`faq-topic ${isOpen ? 'faq-topic--open' : ''}`}
              >
                <button
                  type="button"
                  className="faq-topic__header"
                  onClick={() => handleToggleTopic(topic.id ?? '')}
                  aria-expanded={isOpen}
                >
                  <div className="faq-topic__icon">
                    <QuestionCircleOutlined />
                  </div>
                  <ThemedText variant="h3" className="faq-topic__title">
                    {topic.topic}
                  </ThemedText>
                  <RightOutlined
                    className={`faq-topic__chevron ${
                      isOpen ? 'faq-topic__chevron--open' : ''
                    }`}
                  />
                </button>
                {isOpen ? (
                  <div className="faq-topic__content">
                    <div className="faq-questions">
                      {topic.questions?.map((question) => (
                        <div key={question.id} className="faq-question">
                          <ThemedText
                            variant="h3"
                            className="faq-question__title"
                          >
                            {question.question}
                          </ThemedText>
                          <ThemedText
                            variant="body"
                            className="faq-question__answer"
                          >
                            {question.answer}
                          </ThemedText>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null}
              </div>
            );
          })}
      </div>
    );
  };

  return (
    <>
      <AdvancedHeader
        showBreadCrumbs
        breadCrumbs={[
          {
            active: true,
            title: translate('COMPONENTS.BREADCRUMB.HOME'),
            url: `${getRouteStackPath('HOME', 'HOME_PAGE')}`,
          },
          {
            active: true,
            title: translate('COMPONENTS.BREADCRUMB.MY_ACCOUNT'),
            url: getRouteStackPath('PROFILE', 'PROFILE_PAGE'),
          },
          {
            active: false,
            title: translate('PAGES.MY_ACCOUNT.TABS.FAQ'),
          },
        ]}
      />
      <div className="faq-page">
        <AdvancedPanelContentHeader
          testId="faq-panel-header"
          title={translate('PAGES.MY_ACCOUNT.FAQ.TITLE')}
          description={translate('PAGES.MY_ACCOUNT.FAQ.DESCRIPTION')}
          goBackAction={() => navigate.back()}
        />
        <div className="faq-container">
          {renderContent()}
          {renderContact()}
        </div>
      </div>
    </>
  );
};

export default Faq;
